import React, { memo, ReactElement, useCallback } from 'react';
import { Control, FieldPathByValue, FieldValues, PathValue, useController } from 'react-hook-form';

import ComboboxInput, { ComboboxInputProps } from './combobox-input';

export type FormComboboxInputProps<
  TFieldValues extends FieldValues,
  TPath extends FieldPathByValue<TFieldValues, unknown>,
> = {
  control: Control<TFieldValues>;
  controlled?: boolean;
  customValue?: (value?: PathValue<TFieldValues, TPath>) => string | undefined;
  defaultValue?: PathValue<TFieldValues, TPath>;
  name: TPath;
  onBlur?: () => void;
} & Omit<ComboboxInputProps, 'defaultValue' | 'onBlur' | 'onChange' | 'value'>;

const FormComboboxInput = <
  TFieldValues extends FieldValues,
  TPath extends FieldPathByValue<TFieldValues, unknown>,
>({
  control,
  defaultValue,
  name,
  customValue,
  onBlur,
  ...props
}: FormComboboxInputProps<TFieldValues, TPath>): null | ReactElement => {
  const { field, fieldState } = useController({
    control,
    defaultValue,
    name,
  });
  const handleChange = useCallback((value: string) => field.onChange(() => value), [field]);

  const handleBlur = useCallback(() => {
    field?.onBlur();
    onBlur?.();
  }, [field, onBlur]);
  return (
    <ComboboxInput
      {...props}
      {...field}
      errorMessage={fieldState.error?.message ?? fieldState.error?.type}
      onBlur={handleBlur}
      onChange={handleChange}
      value={customValue ? (customValue(field.value) ?? '') : field.value || ''}
    />
  );
};

export default memo(FormComboboxInput) as typeof FormComboboxInput;
